<template>
    <v-card class="seed-stock" dark elevation="0">
        <v-overlay :value="loading" absolute opacity="0.85" class="seed-stock__loading">
            <v-progress-circular class="ma-3" indeterminate large color="#f0784c" />
        </v-overlay>
        <header class="seed-stock__header mb-5">
            <span class="seed-stock__eyebrow">Seed stock</span>
            <h2>Request seed stock</h2>
            <p>Select the component categories you need.</p>
        </header>
        <v-card-text class="seed-stock__body">
            <div v-if="byCategory.length" class="seed-stock__categories" role="group" aria-label="Component categories">
                <button
                    v-for="category in byCategory"
                    :key="category"
                    type="button"
                    class="seed-stock__category"
                    :class="{ 'seed-stock__category--selected': selected.includes(category) }"
                    :aria-pressed="selected.includes(category) ? 'true' : 'false'"
                    :disabled="loading"
                    @click="toggleCategory(category)"
                >
                    <span class="seed-stock__category-name">{{ category }}</span>
                    <v-icon small aria-hidden="true" class="seed-stock__category-icon">{{
                        selected.includes(category) ? mdiCheck : mdiPlus
                    }}</v-icon>
                </button>
            </div>
            <div v-else class="seed-stock__empty">
                <span>No categories available</span>
            </div>
        </v-card-text>
        <div class="seed-stock__selection-summary" role="status" aria-live="polite">
            {{ selected.length }} categor{{ selected.length === 1 ? 'y' : 'ies' }} selected
        </div>
        <v-card-actions class="seed-stock__actions">
            <v-btn class="seed-stock__cancel" elevation="0" text :disabled="loading" @click="$emit('cancel')"
                >Cancel</v-btn
            >
            <v-spacer></v-spacer>
            <v-btn
                class="seed-stock__submit"
                color="#db5120"
                dark
                elevation="0"
                :disabled="loading || !selected.length"
                @click="submitStock"
                >Submit request</v-btn
            >
        </v-card-actions>
    </v-card>
</template>

<script>
import gql from 'graphql-tag'
import { mdiCheck, mdiPlus } from '@mdi/js'

export default {
    name: 'SeedStock',
    props: {
        confirmedCustomer: {
            type: Object,
            required: false,
            default: () => {}
        },
        confirmedCategories: {
            type: Array,
            required: false,
            default: () => []
        }
    },
    apollo: {
        querySMCustomerCategories: {
            query: gql`
                query Query($customerId: String!) {
                    querySMCustomerCategories(customerId: $customerId)
                }
            `,
            variables() {
                return {
                    customerId: this.$auth.user.org.branchId
                }
            },
            update(data) {
                this.byCategory = data.querySMCustomerCategories
                this.loading = false
            }
        }
    },
    data() {
        return {
            mdiCheck,
            mdiPlus,
            loading: true,
            selected: [...this.confirmedCategories],
            byCategory: []
        }
    },
    methods: {
        toggleCategory(category) {
            if (this.loading) return
            this.selected = this.selected.includes(category)
                ? this.selected.filter((item) => item !== category)
                : [...this.selected, category]
        },
        submitStock() {
            if (this.loading || !this.selected.length) return
            this.loading = true
            const input = {
                confirmedCategories: this.selected,
                smConfirmed: true,
                address: this.confirmedCustomer.address,
                contact: {
                    company: this.confirmedCustomer.contact.company,
                    firstName: this.confirmedCustomer.contact.firstName,
                    lastName: this.confirmedCustomer.contact.lastName,
                    email: this.confirmedCustomer.contact.email,
                    phone: this.confirmedCustomer.contact.phone
                }
            }
            this.$apollo
                .mutate({
                    mutation: gql`
                        mutation UpdateAccountOrganization($input: AccountOrganizationInput!, $orgId: String!) {
                            updateAccountOrganization(input: $input, orgId: $orgId) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: {
                        input: input,
                        orgId: this.$auth.user.org.branchId
                    }
                })
                .then((data) => {
                    if (data && data.data) data.data.confirmedCategories = this.selected
                    this.$emit('submitted', data)
                })
                .catch((e) => {
                    this.$emit('errorSubmit')
                })
                .finally(() => {
                    this.loading = false
                })
        }
    }
}
</script>

<style scoped>
.seed-stock.v-card {
    display: flex;
    max-height: calc(100vh - 48px);
    max-height: calc(100dvh - 48px);
    flex-direction: column;
    overflow: hidden;
    color: #f2f4fa;
    background: #282b33;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.seed-stock__header {
    flex-shrink: 0;
    padding: 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}
.seed-stock__eyebrow {
    color: #f0784c;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1.2px;
    text-transform: uppercase;
}
.seed-stock__header h2 {
    margin: 3px 0 4px;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.5px;
    line-height: 1.3;
}
.seed-stock__header p {
    margin: 0;
    color: #b9bfcc;
    font-size: 12px;
    line-height: 1.5;
}
.seed-stock__body {
    min-height: 0;
    padding: 12px 16px;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-color: #f0784c #22252d;
    scrollbar-width: thin;
}
.seed-stock__categories {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
}
.seed-stock__category {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
    min-height: 36px;
    padding: 7px 10px;
    color: #e2e5ec;
    background: #22252d;
    border: 1px solid #484c57;
    border-radius: 6px;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s, border-color 0.15s;
}
.seed-stock__category:hover:not(:disabled) {
    border-color: #f0784c;
    background: #343039;
}
.seed-stock__category--selected {
    border-color: #f0784c;
    background: rgba(219, 81, 32, 0.12);
    color: #ffe0d3;
}
.seed-stock__category-icon.v-icon {
    flex: 0 0 auto;
    color: #a6acb9;
}
.seed-stock__category--selected .seed-stock__category-icon {
    color: #f0784c;
}
.seed-stock__category:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.seed-stock__category:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 2px;
}
.seed-stock__category-name {
    min-width: 0;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.5;
    overflow-wrap: anywhere;
}
.seed-stock__empty {
    padding: 20px 12px;
    color: #b9bfcc;
    background: #22252d;
    border: 1px dashed rgba(255, 255, 255, 0.16);
    border-radius: 6px;
    font-size: 12px;
    text-align: center;
}
.seed-stock__selection-summary {
    flex-shrink: 0;
    padding: 0 16px 10px;
    color: #b9bfcc;
    font-size: 11px;
}
.seed-stock__actions {
    flex-shrink: 0;
    gap: 12px;
    padding: 10px 16px;
    background: #22252d;
    border-top: 1px solid rgba(255, 255, 255, 0.09);
}
.seed-stock__actions .v-btn {
    min-height: 34px;
    height: 34px;
    border-radius: 7px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
}
.seed-stock__cancel.v-btn {
    color: #c7cbd4;
}
.seed-stock__submit.v-btn:not(.v-btn--disabled):hover {
    background-color: #b84420 !important;
}
.seed-stock__actions .v-btn:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
@media (max-width: 599px) {
    .seed-stock.v-card {
        max-height: 100vh;
        max-height: 100dvh;
    }
}
@media (max-width: 359px) {
    .seed-stock__categories {
        grid-template-columns: 1fr;
    }
}
</style>
